import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Phone, Mail, GraduationCap, ChevronDown, Menu, X, BookOpen, MessageCircle } from 'lucide-react';
import { CATEGORIES } from '../../data/mock';
import { useSiteLanguage } from '../../context/SiteLanguageContext';
import SaleBanner from '../SaleBanner';

const NAV_LINKS = [
  { labelKey: 'header.courses',              href: '/courses',               hasDropdown: true },
  { labelKey: 'header.universityProgression', href: '/university-progression' },
  { labelKey: 'header.aboutUs',              href: '/about' },
  { labelKey: 'header.feesPricing',          href: '/pricing' },
  { labelKey: 'header.contact',              href: '/contact' },
];

export default function Header() {
  const [mobileOpen, setMobileOpen]       = useState(false);
  const [coursesDropdown, setCoursesDropdown] = useState(false);
  const [scrolled, setScrolled]           = useState(false);
  const dropdownRef                        = useRef(null);
  const location                           = useLocation();
  const { language, languages, setLanguage, t } = useSiteLanguage();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); setCoursesDropdown(false); }, [location]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target))
        setCoursesDropdown(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isActive = (href) =>
    href === '/' ? location.pathname === '/' : location.pathname.startsWith(href);

  return (
    <header className="sticky top-0 z-50">
      <SaleBanner />

      {/* Slim contact/utility bar */}
      <div className="bg-[#F7F7F7] border-b border-gray-200 text-xs text-gray-500 py-1.5 px-4 hidden sm:block">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-5">
            <a href="tel:+442033555646" className="flex items-center gap-1.5 hover:text-[#4B286D] transition-colors" dir="ltr">
              <Phone size={11} /> +44 (0) 20 3355 5646
            </a>
            <a href="https://wa.me/447859151818" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 hover:text-[#4B286D] transition-colors">
              <MessageCircle size={11} /> WhatsApp
            </a>
            <a href="mailto:info@atlas-academy.co.uk" className="flex items-center gap-1.5 hover:text-[#4B286D] transition-colors hidden md:flex">
              <Mail size={11} /> info@atlas-academy.co.uk
            </a>
          </div>
          <div className="flex items-center gap-4">
            <label className="hidden lg:flex items-center gap-1.5">
              <span>{t('language.label', 'Language')}</span>
              <select
                value={language}
                onChange={e => setLanguage(e.target.value)}
                className="border border-gray-300 rounded px-1.5 py-0.5 text-gray-700 bg-white text-xs focus:outline-none focus:border-[#4B286D]"
              >
                {languages.map(opt => (
                  <option key={opt.code} value={opt.code} className="text-gray-900">
                    {t(`language.${opt.code}`, opt.code)}
                  </option>
                ))}
              </select>
            </label>
            <Link to="/student-support" className="hover:text-[#4B286D] transition-colors">
              {t('header.studentPortal', 'Student Portal')}
            </Link>
          </div>
        </div>
      </div>

      {/* Main navigation */}
      <nav className={`bg-white transition-all duration-200 ${scrolled ? 'shadow-md' : 'border-b border-gray-100'}`}>
        <div className="max-w-7xl mx-auto px-4 flex items-center justify-between h-[66px]">

          {/* Logo */}
          <Link to="/" className="shrink-0">
            <img
              src="/images/atlas_primary_horizontal.png"
              alt="Atlas Academy"
              className="h-10 w-auto object-contain"
            />
          </Link>

          {/* Desktop nav links */}
          <div className="hidden lg:flex items-center gap-0.5">
            {NAV_LINKS.map((link) =>
              link.hasDropdown ? (
                <div key={link.href} className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => setCoursesDropdown(!coursesDropdown)}
                    className={`flex items-center gap-1 px-3.5 py-2 rounded-lg text-sm font-semibold transition-colors ${
                      isActive(link.href) ? 'text-[#4B286D]' : 'text-gray-700 hover:text-[#4B286D]'
                    }`}
                  >
                    {t(link.labelKey, 'Courses')}
                    <ChevronDown size={14} className={`transition-transform ${coursesDropdown ? 'rotate-180' : ''}`} />
                  </button>

                  {coursesDropdown && (
                    <div className="absolute top-full left-0 mt-1.5 w-64 bg-white rounded-2xl shadow-2xl border border-gray-100 py-2 z-50">
                      <Link to="/courses" className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-[#F3ECF9] hover:text-[#4B286D] transition-colors font-semibold">
                        <BookOpen size={14} />{t('header.allCourses', 'All Courses')}
                      </Link>
                      <div className="border-t border-gray-100 my-1.5" />
                      <p className="px-4 pb-1 text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                        {t('courses.filter.category', 'By Category')}
                      </p>
                      {CATEGORIES.filter(c => c !== 'All Courses').map(cat => (
                        <Link
                          key={cat}
                          to={`/courses?category=${encodeURIComponent(cat)}`}
                          className="block px-4 py-2 text-sm text-gray-600 hover:bg-[#F3ECF9] hover:text-[#4B286D] transition-colors"
                        >
                          {cat}
                        </Link>
                      ))}
                      <div className="border-t border-gray-100 my-1.5" />
                      <p className="px-4 pb-1 text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                        {t('header.awardingBodies', 'Awarding Bodies')}
                      </p>
                      <Link to="/awarding-bodies/othm"   className="block px-4 py-2 text-sm text-gray-600 hover:bg-[#F3ECF9] hover:text-[#4B286D] transition-colors">OTHM Qualifications</Link>
                      <Link to="/awarding-bodies/qualifi" className="block px-4 py-2 text-sm text-gray-600 hover:bg-[#F3ECF9] hover:text-[#4B286D] transition-colors">QUALIFI Qualifications</Link>
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`px-3.5 py-2 rounded-lg text-sm font-semibold transition-colors ${
                    isActive(link.href) ? 'text-[#4B286D]' : 'text-gray-700 hover:text-[#4B286D]'
                  }`}
                >
                  {t(link.labelKey, link.href)}
                </Link>
              )
            )}
          </div>

          {/* Desktop CTA */}
          <Link
            to="/apply"
            className="hidden lg:inline-flex items-center gap-2 bg-[#DE00A5] hover:bg-[#B80089] text-white px-5 py-2.5 rounded-full text-sm font-bold transition-colors shadow-sm"
          >
            {t('header.applyNow', 'Apply Now')}
          </Link>

          {/* Mobile hamburger */}
          <button
            className="lg:hidden p-2 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label={t('header.toggleMenu', 'Toggle menu')}
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="lg:hidden border-t border-gray-100 bg-white pb-5">
            <div className="max-w-7xl mx-auto px-4 pt-3 flex flex-col gap-1">
              {/* Mobile language */}
              <label className="flex items-center justify-between gap-3 px-3 py-2 text-sm text-gray-600">
                <span>{t('language.label', 'Language')}</span>
                <select
                  value={language}
                  onChange={e => setLanguage(e.target.value)}
                  className="border border-gray-200 rounded px-2 py-1 text-sm text-gray-700 focus:outline-none"
                >
                  {languages.map(opt => (
                    <option key={opt.code} value={opt.code}>{t(`language.${opt.code}`, opt.code)}</option>
                  ))}
                </select>
              </label>

              {[...NAV_LINKS, { labelKey: 'header.applyNow', href: '/apply', highlight: true }].map(link => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`px-3 py-2.5 rounded-xl text-sm font-semibold transition-colors ${
                    link.highlight
                      ? 'bg-[#DE00A5] text-white text-center mt-1'
                      : isActive(link.href)
                        ? 'text-[#4B286D] bg-[#F3ECF9]'
                        : 'text-gray-700 hover:bg-gray-50 hover:text-[#4B286D]'
                  }`}
                >
                  {t(link.labelKey, link.href)}
                </Link>
              ))}

              <div className="mt-3 pt-3 border-t border-gray-100 flex flex-col gap-2 text-sm text-gray-500">
                <a href="tel:+442033555646" dir="ltr" className="flex items-center gap-2 hover:text-[#4B286D]">
                  <Phone size={13} /> +44 (0) 20 3355 5646
                </a>
                <a href="https://wa.me/447859151818" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-green-600 hover:text-green-700">
                  <MessageCircle size={13} /> WhatsApp: +44 (0) 78 5915 1818
                </a>
                <a href="mailto:info@atlas-academy.co.uk" className="flex items-center gap-2 hover:text-[#4B286D]">
                  <Mail size={13} /> info@atlas-academy.co.uk
                </a>
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}
